import {useState} from "react";
import {ChevronDown, ChevronRight, Download} from "lucide-react";
import {DataState} from "../../shared/components/DataState";
import {useTranslation} from "../../shared/i18n";
import {formatMoney, formatNumber} from "../../shared/utils/format";
import {buildPayReportDownloadUrl, exportPayReportSpreadsheet, loadAllPayReportRows} from "./api";

const MONEY_KEYS = ["sum", "commission", "partner_sum", "bonus_pay", "balance_add_pay", "return_sum"];

const detailFiltersForGroup = (filters, placeId) =>
    Object.fromEntries(
        Object.entries({
            date_from: filters.date_from,
            date_to: filters.date_to,
            branch: filters.branch,
            place: placeId,
        }).filter(([, value]) => value !== "" && value !== null && value !== undefined),
    );

export const GroupedPayTable = ({
    groups,
    detailColumns,
    filters = {},
    loading,
    emptyText,
}) => {
    const {t} = useTranslation();
    const resolvedEmptyText = emptyText || t("money.finance.datasets.pay.empty");
    const [openId, setOpenId] = useState(null);
    const [detailsByPlace, setDetailsByPlace] = useState({});
    const [loadingPlace, setLoadingPlace] = useState(null);
    const [detailError, setDetailError] = useState("");
    const [exportingPlace, setExportingPlace] = useState(null);
    const [exportError, setExportError] = useState("");

    if (loading) {
        return (
            <div className="admin-table-skeleton" aria-label={t("common.table.loading")}>
                {Array.from({length: 6}).map((_, index) => (
                    <span key={index} />
                ))}
            </div>
        );
    }

    if (!groups.length) {
        return <DataState title={resolvedEmptyText} copy={t("common.state.emptyHint")} />;
    }

    const toggleGroup = async (group) => {
        const placeId = String(group.place_id);
        if (openId === placeId) {
            setOpenId(null);
            return;
        }
        setOpenId(placeId);
        setDetailError("");
        if (detailsByPlace[placeId]) {
            return;
        }
        setLoadingPlace(placeId);
        try {
            const rows = await loadAllPayReportRows(detailFiltersForGroup(filters, placeId));
            setDetailsByPlace((current) => ({...current, [placeId]: rows}));
        } catch (_error) {
            setDetailError(t("money.finance.groups.detailError"));
        } finally {
            setLoadingPlace(null);
        }
    };

    const exportGroup = async (event, group) => {
        event.stopPropagation();
        const placeId = String(group.place_id);
        if (exportingPlace) {
            return;
        }
        setExportingPlace(placeId);
        setExportError("");
        try {
            await exportPayReportSpreadsheet(
                group.download || buildPayReportDownloadUrl(detailFiltersForGroup(filters, placeId)),
                group.place_name || "pay-report",
            );
        } catch (_error) {
            setExportError(t("money.finance.groups.exportError"));
        } finally {
            setExportingPlace(null);
        }
    };

    return (
        <div className="admin-table-wrap">
            {exportError ? <p className="admin-card-note" role="alert">{exportError}</p> : null}
            {detailError ? <p className="admin-card-note" role="alert">{detailError}</p> : null}
            <table className="admin-table admin-grouped-table">
                <thead>
                    <tr>
                        <th className="admin-table-toggle" aria-hidden="true" />
                        <th>{t("money.finance.groups.place")}</th>
                        <th>{t("money.finance.groups.operations")}</th>
                        <th>{t("money.finance.groups.amount")}</th>
                        <th>{t("money.finance.groups.commission")}</th>
                        <th>{t("money.finance.groups.partner")}</th>
                        <th>Plus</th>
                        <th>Extra</th>
                        <th>{t("money.finance.groups.refund")}</th>
                        <th className="admin-table-export">{t("money.finance.groups.export")}</th>
                    </tr>
                </thead>
                {groups.map((group) => {
                    const placeId = String(group.place_id);
                    const isOpen = openId === placeId;
                    const details = detailsByPlace[placeId] || [];
                    const isLoadingDetails = loadingPlace === placeId;
                    return (
                        <tbody key={placeId} className={`admin-table-group ${isOpen ? "is-open" : ""}`}>
                            <tr
                                className="admin-table-group-summary"
                                onClick={() => toggleGroup(group)}
                            >
                                <td className="admin-table-toggle">
                                    <button
                                        type="button"
                                        className="admin-table-expand"
                                        aria-expanded={isOpen}
                                        aria-label={isOpen ? t("money.finance.groups.collapse", {name: group.place_name}) : t("money.finance.groups.expand", {name: group.place_name})}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            toggleGroup(group);
                                        }}
                                    >
                                        {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                                    </button>
                                </td>
                                <td>
                                    <strong>{group.place_name}</strong>
                                </td>
                                <td>{formatNumber(group.count)}</td>
                                {MONEY_KEYS.map((key) => (
                                    <td key={key}>
                                        <span className={key === "sum" ? "admin-strong" : undefined}>
                                            {formatMoney(group[key])}
                                        </span>
                                    </td>
                                ))}
                                <td className="admin-table-export">
                                    <button
                                        type="button"
                                        className="admin-button admin-button-secondary admin-table-export-button"
                                        disabled={exportingPlace === placeId}
                                        onClick={(event) => exportGroup(event, group)}
                                    >
                                        <Download size={14} /> {exportingPlace === placeId ? "…" : t("money.finance.groups.export")}
                                    </button>
                                </td>
                            </tr>
                            {isOpen ? (
                                <tr className="admin-table-group-details">
                                    <td colSpan={10}>
                                        {isLoadingDetails ? (
                                            <div className="admin-table-skeleton" aria-label={t("money.finance.groups.loadingDetails")}>
                                                {Array.from({length: 3}).map((_, index) => (
                                                    <span key={index} />
                                                ))}
                                            </div>
                                        ) : details.length ? (
                                            <table className="admin-table admin-grouped-detail-table">
                                                <thead>
                                                    <tr>
                                                        {detailColumns.map((column) => (
                                                            <th key={column.key}>{column.label}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {details.map((row, index) => (
                                                        <tr key={row.id ?? `${placeId}-${index}`}>
                                                            {detailColumns.map((column) => (
                                                                <td key={column.key}>
                                                                    {column.render ? column.render(row, index) : row[column.key] ?? "—"}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <p className="admin-card-note">{t("money.finance.groups.emptyDetails")}</p>
                                        )}
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    );
                })}
            </table>
        </div>
    );
};
